<template>
  <div
    id="login_page"
    v-hotkey="keymap"
    class="student-login"
  >
    <div class="student-login__background" aria-hidden="true">
      <span
        class="student-login__shape student-login__shape--primary"
      ></span>
      <span
        class="student-login__shape student-login__shape--corner"
      ></span>
      <span
        class="student-login__shape student-login__shape--secondary"
      ></span>
    </div>
    <v-row class="student-login__row" align="center" justify="center">
      <v-col class="student-login__column" cols="12">
        <div class="student-login__logo-wrap">
          <HolidayDoodle class="student-login__doodle" />
          <img
            class="student-login__logo"
            src="../../../style/img/logo.svg"
            alt="Red Fox Home School"
          />
        </div>
        <v-card class="student-login__card" elevation="0">
          <header class="student-login__header">
            <span class="student-login__icon">
              <v-icon size="27">mdi-account-outline</v-icon>
            </span>
            <div>
              <h1>Вхід до кабінету</h1>
              <p>Введіть дані свого облікового запису</p>
            </div>
          </header>

          <v-form
            ref="form"
            v-model="valid"
            :lazy-validation="lazy"
            class="student-login__form"
            @submit.prevent="login"
          >
            <v-alert
              v-if="errors"
              class="student-login__error"
              dense
              text
              type="error"
            >
              {{ errors }}
            </v-alert>

            <div class="student-login__field">
              <label for="student-login-email">E-mail</label>
              <v-text-field
                id="student-login-email"
                v-model="email"
                :rules="activeEmailRules"
                autocomplete="username"
                background-color="#fff"
                hide-details="auto"
                outlined
                placeholder="name@example.com"
                prepend-inner-icon="mdi-email-outline"
                required
                @input="errors = ''"
              />
            </div>

            <div class="student-login__field">
              <label for="student-login-password">Пароль</label>
              <v-text-field
                id="student-login-password"
                v-model="password"
                :rules="activePasswordRules"
                :append-icon="
                  togglePassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                "
                :type="togglePassword ? 'text' : 'password'"
                autocomplete="current-password"
                background-color="#fff"
                hide-details="auto"
                outlined
                placeholder="Введіть пароль"
                prepend-inner-icon="mdi-lock-outline"
                @click:append="togglePassword = !togglePassword"
                @input="errors = ''"
              />
            </div>

            <button
              type="submit"
              class="student-login__submit"
              :disabled="loggingIn"
            >
              <v-progress-circular
                v-if="loggingIn"
                color="#fff"
                indeterminate
                size="20"
                width="3"
              />
              <template v-else>
                Увійти
                <v-icon color="#fff" size="20">mdi-arrow-right</v-icon>
              </template>
            </button>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
    <label
      v-if="showStageDoodlePreview"
      class="student-login__preview-control"
    >
      <span>Stage: святковий дудл</span>
      <select
        v-model="holidayPreviewSelection"
        @change="applyHolidayPreview"
      >
        <option
          v-for="option in holidayPreviewOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </label>
  </div>
</template>

<script>
import statesEnum from '@/enums/statesEnum';
import RestService from '@/services/RestService';
import appMixin from '@/components/Mixin/appMixin';
import HolidayDoodle from '@/components/Base/HolidayDoodle/HolidayDoodle';
export default {
  name: 'LoginPage',
  components: { HolidayDoodle },
  mixins: [appMixin],
  data() {
    return {
      lazy: true,
      valid: true,
      validationRequested: false,
      togglePassword: false,
      password: '',
      errors: '',
      loggingIn: false,
      holidayPreviewSelection: '',
      holidayPreviewOptions: [
        { value: '', label: 'За календарем' },
        { value: 'none', label: 'Без дудла' },
        { value: 'knowledge-day', label: 'День знань' },
        { value: 'educators-day', label: 'День працівників освіти' },
        { value: 'saint-nicholas-day', label: 'День святого Миколая' },
        { value: 'winter-holiday', label: 'Різдвяно-зимові свята' },
        { value: 'easter', label: 'Великдень' },
        { value: 'vyshyvanka-day', label: 'День вишиванки' },
        { value: 'last-bell', label: 'Останній дзвоник' },
        { value: 'independence-day', label: 'День Незалежності' },
      ],
      required: (value) => !!value || 'Введіть пароль',
      min: (v) => v.length >= 8 || 'Min 8 characters',
      email: '',
      emailRules: [
        (v) => !!v || 'Введіть e-mail',
        (v) => /.+@.+\..+/.test(v) || 'Перевірте формат e-mail',
      ],
    };
  },
  mounted() {
    this.$store.dispatch('ThemeStore/initialize');
    this.holidayPreviewSelection =
      this.$store.getters['ThemeStore/getPreviewHolidayCode'] || '';
    const demoToken = this.$store.getters['ConfigStore/getDemoToken'];
    if (demoToken) {
      this.guestLogin(demoToken);
    }
  },
  computed: {
    showStageDoodlePreview() {
      return (
        this.$store.getters['ConfigStore/isStage'] ||
        this.$store.getters['ConfigStore/isLocal']
      );
    },
    activeEmailRules() {
      return this.validationRequested ? this.emailRules : [];
    },
    activePasswordRules() {
      return this.validationRequested ? [this.required] : [];
    },
    user() {
      return this.$store.getters['UserStore/getUser'];
    },
    keymap() {
      return {
        enter: this.login,
      };
    },
    routeAfterLogin() {
      return this.$store.getters['ConfigStore/getRouteAfterLogin'];
    },
  },

  methods: {
    async applyHolidayPreview() {
      await this.$store.dispatch(
        'ThemeStore/setHolidayPreview',
        this.holidayPreviewSelection
      );
    },
    async login() {
      if (this.loggingIn) return;
      this.validationRequested = true;
      await this.$nextTick();
      if (!this.$refs.form.validate()) return;
      this.loggingIn = true;
      this.errors = '';
      try {
        const email = this.email ? this.email.trim() : '';
        const payload = {
          email,
          password: this.password,
        };
        await this.$store.dispatch('UserStore/loginUser', payload);
        this.afterLogin();
      } catch (error) {
        this.errors = 'Невірний логін або пароль';
        console.error(error);
      } finally {
        this.loggingIn = false;
      }
    },
    guestLogin(demoToken) {
      this.$store.dispatch('UserStore/createGuestUser', {
        token: demoToken,
      });
      this.afterLogin();
    },
  },
};
</script>

<style lang="less" scoped>
.student-login {
  align-items: center;
  background-color: var(--theme-app-background);
  box-sizing: border-box;
  display: flex;
  min-height: 100vh;
  padding: 28px 24px;
}
.student-login__row {
  margin: 0;
  position: relative;
  width: 100%;
  z-index: 1;
}
.student-login__column {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 460px;
  padding: 12px;
}
.student-login__logo {
  display: block;
  flex: 0 0 auto;
  height: auto;
  position: relative;
  width: 210px;
  z-index: 4;
}
.student-login__logo-wrap {
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
}
.student-login__doodle {
  height: 138px;
  top: 41%;
  width: 480px;
  z-index: 2;
}
.student-login__doodle::v-deep .holiday-doodle__art {
  height: 138px;
  width: 450px;
}
.student-login__doodle.holiday-doodle--vyshyvanka-day {
  left: 50%;
  top: 42%;
  transform: translate(-50%, -50%);
}
.student-login__doodle.holiday-doodle--vyshyvanka-day::v-deep
  .holiday-doodle__art {
  width: 470px;
}
.student-login__card {
  background: rgba(255, 255, 255, 0.96) !important;
  border: 1px solid var(--theme-border-decorative) !important;
  border-radius: 20px !important;
  box-shadow: 0 18px 48px rgba(31, 41, 55, 0.13) !important;
  overflow: hidden;
  padding: 28px;
  width: 100%;
}
.student-login__header {
  align-items: center;
  display: flex;
  gap: 14px;
  margin-bottom: 25px;
}
.student-login__icon {
  align-items: center;
  background: var(--theme-banner-background);
  border-radius: 13px;
  display: flex;
  flex: 0 0 auto;
  height: 52px;
  justify-content: center;
  width: 52px;
}
.student-login__icon::v-deep .v-icon {
  color: var(--theme-header-background) !important;
}
.student-login__header h1 {
  color: #172033;
  font-size: 23px;
  line-height: 1.25;
  margin: 0 0 4px;
}
.student-login__header p {
  color: #7a8592;
  font-size: 13px;
  margin: 0;
}
.student-login__form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.student-login__error {
  border-radius: 10px;
  font-size: 13px;
  margin: 0 !important;
}
.student-login__field > label {
  color: #344054;
  display: block;
  font-size: 13px;
  font-weight: 750;
  margin-bottom: 7px;
}
.student-login__field::v-deep .v-input__slot {
  align-items: center;
  border-radius: 11px !important;
  min-height: 56px !important;
}
.student-login__field::v-deep .v-input__prepend-inner,
.student-login__field::v-deep .v-input__append-inner {
  align-self: center;
  margin-top: 0 !important;
}
.student-login__field::v-deep .v-input__prepend-inner {
  margin-right: 12px;
}
.student-login__field::v-deep .v-text-field__slot {
  align-items: center;
}
.student-login__field::v-deep .v-text-field__slot input {
  line-height: 24px;
  padding-bottom: 0;
  padding-top: 0;
}
.student-login__field::v-deep .v-input__slot fieldset {
  border-color: var(--theme-border-decorative);
}
.student-login__field::v-deep .v-input--is-focused .v-input__slot fieldset {
  border-color: var(--theme-header-background) !important;
}
.student-login__field::v-deep .v-input.error--text .v-input__slot fieldset {
  border-color: #ff5252 !important;
  border-width: 2px;
}
.student-login__field::v-deep .v-input--is-focused .v-icon {
  color: var(--theme-header-background) !important;
}
.student-login__field::v-deep .v-text-field__details {
  padding: 6px 0 0;
}
.student-login__submit {
  align-items: center;
  background: var(--theme-header-background);
  border: 0;
  border-radius: 11px;
  color: #fff;
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 850;
  gap: 9px;
  justify-content: center;
  min-height: 50px;
  padding: 10px 18px;
  transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
  width: 100%;
}
.student-login__submit:hover:not(:disabled) {
  box-shadow: 0 8px 20px rgba(31, 41, 55, 0.18);
  filter: brightness(0.9);
  transform: translateY(-1px);
}
.student-login__submit:disabled {
  cursor: wait;
  opacity: 0.72;
}
.student-login__submit:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 3px;
}
#login_page.student-login {
  background-image: none;
  height: 100vh;
  inset: 0;
  margin: 0;
  max-width: none;
  overflow-y: auto;
  position: fixed;
  width: 100vw;
  transition: background-color 0.25s ease;
}

.student-login__background {
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
}

.student-login__shape {
  display: block;
  position: absolute;
  transition: background-color 0.25s ease;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
}

.student-login__shape--primary {
  aspect-ratio: 1209 / 823;
  background-color: var(--theme-banner-background);
  bottom: -100px;
  left: 0;
  max-width: 1209px;
  width: 76vw;
  -webkit-mask-image: url("../../../style/img/green.svg");
  mask-image: url("../../../style/img/green.svg");
}

.student-login__shape--corner {
  aspect-ratio: 383 / 429;
  background-color: var(--theme-surface-decorative);
  left: -100px;
  max-width: 383px;
  top: 0;
  width: 25vw;
  -webkit-mask-image: url("../../../style/img/orange.svg");
  mask-image: url("../../../style/img/orange.svg");
}

.student-login__shape--secondary {
  aspect-ratio: 933 / 800;
  background-color: var(--theme-accent-decorative);
  bottom: -200px;
  max-width: 933px;
  right: 0;
  width: 59vw;
  -webkit-mask-image: url("../../../style/img/green2.svg");
  mask-image: url("../../../style/img/green2.svg");
}

.student-login__preview-control {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 12px;
  bottom: 18px;
  box-shadow: 0 8px 24px rgba(31, 41, 55, 0.12);
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  position: fixed;
  right: 18px;
  width: 250px;
  z-index: 7;
}

.student-login__preview-control span {
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.student-login__preview-control select {
  background: var(--surface-primary);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 8px;
  color: var(--text-primary);
  min-height: 36px;
  padding: 0 9px;
}

@media (max-width: 600px) {
  #login_page.student-login {
    align-items: center;
    padding: 20px 12px;
  }
  .student-login__shape--primary {
    bottom: -40px;
    width: 115vw;
  }
  .student-login__shape--corner {
    left: -55px;
    width: 58vw;
  }
  .student-login__shape--secondary {
    bottom: -80px;
    width: 92vw;
  }
  .student-login__column {
    max-width: 100%;
    padding: 8px;
  }
  .student-login__logo {
    width: 180px;
  }
  .student-login__doodle {
    height: 108px;
    top: 41%;
    width: 350px;
  }
  .student-login__doodle::v-deep .holiday-doodle__art {
    height: 108px;
    transform: scaleX(1.22);
    width: 330px;
  }
  .student-login__doodle.holiday-doodle--vyshyvanka-day::v-deep
    .holiday-doodle__art {
    width: 345px;
  }
  .student-login__preview-control {
    bottom: 10px;
    left: 12px;
    right: 12px;
    width: auto;
  }
  .student-login__card {
    border-radius: 16px !important;
    padding: 22px 18px;
  }
  .student-login__header {
    align-items: flex-start;
  }
  .student-login__header h1 {
    font-size: 21px;
  }
}

@media (max-width: 600px) and (max-height: 650px) {
  .student-login__column {
    gap: 12px;
  }
  .student-login__logo {
    width: 140px;
  }
}
</style>
